import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, Stethoscope, Wrench, Home, Scale, UtensilsCrossed, HardHat, Building2, Megaphone, Truck, Store, Landmark } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { companyInfo } from '../data/mock';

const smallIndustries = [
  { icon: Stethoscope, label: 'Dental & Medical Offices', slug: 'dental-medical-offices' },
  { icon: Wrench, label: 'Auto Repair Shops', slug: 'auto-repair-shops' },
  { icon: Home, label: 'Real Estate Agencies', slug: 'real-estate-agencies' },
  { icon: Scale, label: 'Law Firms', slug: 'law-firms' },
  { icon: UtensilsCrossed, label: 'Restaurants & Hospitality', slug: 'restaurants-hospitality' },
  { icon: HardHat, label: 'Service Contractors', slug: 'service-contractors' },
];

const midIndustries = [
  { icon: Stethoscope, label: 'Healthcare Groups', slug: 'healthcare-groups' },
  { icon: Building2, label: 'Manufacturing & Supply Chain', slug: 'manufacturing-supply-chain' },
  { icon: Megaphone, label: 'Marketing & Digital Agencies', slug: 'marketing-digital-agencies' },
  { icon: Truck, label: 'Logistics & Transportation', slug: 'logistics-transportation' },
  { icon: Store, label: 'Retail Chains & Franchises', slug: 'retail-chains-franchises' },
  { icon: Landmark, label: 'Financial Services', slug: 'financial-services-insurance' },
];

const DropdownMenu = ({ isOpen, children, align = 'left' }) => {
  const alignClass =
    align === 'center'
      ? 'left-1/2 -translate-x-1/2'
      : align === 'right'
      ? 'right-0'
      : 'left-0';
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 8, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.97 }}
          transition={{ duration: 0.18 }}
          className={`absolute top-full mt-2 bg-[#0A0A0A] rounded-2xl shadow-xl border border-white/[0.08] overflow-hidden z-50 ${alignClass}`}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const dropdownTimeout = useRef(null);
  const lastScrollY = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      setIsScrolled(y > 50);
      // Hide on scroll-down past hero, show on scroll-up. Always show near top.
      if (y < 80) {
        setHidden(false);
      } else if (y > lastScrollY.current + 4) {
        setHidden(true);
      } else if (y < lastScrollY.current - 4) {
        setHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  const goToIndustry = (slug) => {
    navigate(`/industries/${slug}`);
    setIsMenuOpen(false);
    setOpenDropdown(null);
  };

  const handleDropdownEnter = (name) => {
    clearTimeout(dropdownTimeout.current);
    setOpenDropdown(name);
  };

  const handleDropdownLeave = () => {
    dropdownTimeout.current = setTimeout(() => setOpenDropdown(null), 150);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: hidden && !isMenuOpen ? -100 : 0 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        isScrolled ? 'bg-[#0A0A0A]/90 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.4)] py-2' : 'bg-[#0A0A0A]/60 backdrop-blur-sm py-4'
      }`}
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between h-12">
          {/* Logo */}
          <motion.div
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => { navigate('/'); window.scrollTo(0, 0); }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <img
              src={companyInfo.logo}
              alt="Agentic Atoms"
              className="w-12 h-12 rounded-full object-cover bg-white"
            />
            <span className="text-white font-semibold text-base tracking-tight">Agentic Atoms</span>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">

            {/* Industries Dropdown */}
            <div className="relative" onMouseEnter={() => handleDropdownEnter('industries')} onMouseLeave={handleDropdownLeave}>
              <button className={`flex items-center gap-1 text-sm font-medium transition-colors ${openDropdown === 'industries' ? 'text-[#FF6B00]' : 'text-white hover:text-[#FF6B00]'}`} data-testid="nav-industries">
                Industries
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${openDropdown === 'industries' ? 'rotate-180' : ''}`} />
              </button>
              <DropdownMenu isOpen={openDropdown === 'industries'} align="right">
                <div className="w-[480px] p-5">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[9px] font-bold uppercase tracking-[0.2em] text-[#FF6B00] mb-3">Local Business</p>
                      <div className="space-y-0.5">
                        {smallIndustries.map((ind) => {
                          const Icon = ind.icon;
                          return (
                            <button key={ind.slug} onClick={() => goToIndustry(ind.slug)} className="group/item w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left text-sm text-white hover:bg-[#FF6B00]/10 hover:text-[#FF6B00] transition-colors" data-testid={`nav-industry-${ind.slug}`}>
                              <Icon className="w-4 h-4 text-[#FF6B00]/80 group-hover/item:text-[#FF6B00]" /><span>{ind.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <p className="inline-flex items-center px-2 py-0.5 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[9px] font-bold uppercase tracking-[0.2em] text-[#FF6B00] mb-3">Mid-Size Business</p>
                      <div className="space-y-0.5">
                        {midIndustries.map((ind) => {
                          const Icon = ind.icon;
                          return (
                            <button key={ind.slug} onClick={() => goToIndustry(ind.slug)} className="group/item w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-left text-sm text-white hover:bg-[#FF6B00]/10 hover:text-[#FF6B00] transition-colors" data-testid={`nav-industry-${ind.slug}`}>
                              <Icon className="w-4 h-4 text-[#FF6B00]/80 group-hover/item:text-[#FF6B00]" /><span>{ind.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </DropdownMenu>
            </div>

            {/* Scroll links */}
            {[
              { label: 'How It Works', id: 'how-it-works' },
              { label: 'Contact', id: 'contact' },
            ].map((item) => (
              <button key={item.id} onClick={() => scrollToSection(item.id)} className="text-sm font-medium text-white hover:text-[#FF6B00] transition-colors relative group" data-testid={`nav-${item.id}`}>
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#FF6B00] group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <motion.button className="lg:hidden text-white p-2" onClick={() => setIsMenuOpen(!isMenuOpen)} whileTap={{ scale: 0.9 }} data-testid="mobile-menu-button">
            <AnimatePresence mode="wait">
              {isMenuOpen ? (
                <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }} transition={{ duration: 0.2 }}><X className="w-5 h-5" /></motion.div>
              ) : (
                <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} transition={{ duration: 0.2 }}><Menu className="w-5 h-5" /></motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="lg:hidden overflow-hidden">
              <div className="py-4 space-y-1">
                {/* Industries */}
                <div>
                  <button onClick={() => setMobileExpanded(mobileExpanded === 'industries' ? null : 'industries')} className="w-full flex items-center justify-between px-4 py-3 text-white font-medium hover:text-[#FF6B00] hover:bg-[#FF6B00]/10 rounded-xl transition-all">
                    <span>Industries</span><ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === 'industries' ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {mobileExpanded === 'industries' && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                        <p className="inline-flex items-center mx-6 mt-2 mb-1 px-2 py-0.5 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[9px] uppercase tracking-[0.2em] text-[#FF6B00] font-bold">Local Business</p>
                        {smallIndustries.map((ind) => { const Icon = ind.icon; return (
                          <button key={ind.slug} onClick={() => goToIndustry(ind.slug)} className="w-full flex items-center gap-2.5 px-6 py-2 text-sm text-white hover:bg-[#FF6B00]/10 hover:text-[#FF6B00] rounded-lg transition-colors"><Icon className="w-4 h-4 text-[#FF6B00]/80" />{ind.label}</button>
                        ); })}
                        <p className="inline-flex items-center mx-6 mt-3 mb-1 px-2 py-0.5 rounded-full bg-[#FF6B00]/10 border border-[#FF6B00]/30 text-[9px] uppercase tracking-[0.2em] text-[#FF6B00] font-bold">Mid-Size</p>
                        {midIndustries.map((ind) => { const Icon = ind.icon; return (
                          <button key={ind.slug} onClick={() => goToIndustry(ind.slug)} className="w-full flex items-center gap-2.5 px-6 py-2 text-sm text-white hover:bg-[#FF6B00]/10 hover:text-[#FF6B00] rounded-lg transition-colors"><Icon className="w-4 h-4 text-[#FF6B00]/80" />{ind.label}</button>
                        ); })}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                {/* Simple links */}
                {[{ label: 'How It Works', id: 'how-it-works' }, { label: 'Contact', id: 'contact' }].map((item) => (
                  <button key={item.id} onClick={() => scrollToSection(item.id)} className="w-full text-left px-4 py-3 text-white font-medium hover:text-[#FF6B00] hover:bg-[#FF6B00]/10 rounded-xl transition-all">{item.label}</button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
